import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import { OTB_DEFAULT_VALUE } from 'frontend-utils/utils/constants/constant-values';
import changeColorClass from 'lumen/helpers/change-color-class';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import type { Occupancy } from 'lumen/models';
import type { OccupancyChange } from 'lumen/models/occupancy';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';

// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function calculateDifference(value1, value2) {
  if (value1 === null || value2 === null) {
    return null;
  }

  if (value1 === OTB_DEFAULT_VALUE || value2 === OTB_DEFAULT_VALUE) {
    return null;
  }

  return value1 - value2;
}

interface GeneralTooltipsTooltipOtbSignature {
  Args: {
    changeRowLabel: unknown;
    currentRowLabel: unknown;
    occupancy?: Occupancy;
    referenceOccupancy?: OccupancyChange;
    rentalOccupancy: unknown;
    rentalReferenceOccupancy: unknown;
    showAvailableRooms?: boolean;
    showCapacity?: boolean;
    showChangesRow?: boolean;
    showReferenceRow?: boolean;
    showShortTermRental?: boolean;
    showUpdateTime?: boolean;
  };
}

export default class GeneralTooltipsTooltipOtb extends Component<GeneralTooltipsTooltipOtbSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeRowLabel() {
    return (
      this.args.changeRowLabel ?? this.intl.t('rates.changes.change_tooltip')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changes() {
    // If there is no reference return an empty changes set
    if (
      !this.args.showChangesRow ||
      !this.args.referenceOccupancy ||
      this.args.referenceOccupancy.message === 'general.missing'
    ) {
      return null;
    }

    const changes = {
      // @ts-expect-error: Incorrect type
      occupancy: this.args.occupancy.occupancyChangeVersusPercentage(
        this.args.referenceOccupancy,
      ),
      // @ts-expect-error: Incorrect type
      roomsBooked: this.args.occupancy.roomsBookedChangeVersus(
        this.args.referenceOccupancy,
      ),
    };
    if (this.access.hasFeature('pms.transient_group_split')) {
      // @ts-expect-error: Incorrect type
      changes.roomsBookedIndividual = calculateDifference(
        // @ts-expect-error: Incorrect type
        this.args.occupancy.roomsBookedIndividual,
        this.args.referenceOccupancy.rooms_booked_individual,
      );
      // @ts-expect-error: Incorrect type
      changes.roomsBookedGroup = calculateDifference(
        // @ts-expect-error: Incorrect type
        this.args.occupancy.roomsBookedGroup,
        this.args.referenceOccupancy.rooms_booked_group,
      );
    }
    if (this.args.showAvailableRooms) {
      const roomsBooked = this.args.referenceOccupancy.rooms_booked;
      const referenceInventoryExclOOO =
        this.args.referenceOccupancy.total_capacity -
        this.args.referenceOccupancy.rooms_out_of_order;
      const referenceAvailableRooms = calculateDifference(
        referenceInventoryExclOOO,
        roomsBooked,
      );
      // @ts-expect-error: Incorrect type
      changes.availableRooms = calculateDifference(
        // @ts-expect-error: Incorrect type
        this.args.occupancy.availableRooms,
        referenceAvailableRooms,
      );
    }
    if (this.showMarketBenchmark) {
      // @ts-expect-error: Incorrect type
      changes.benchmarkValue =
        // @ts-expect-error: Incorrect type
        this.args.occupancy.benchmarkChangeVersusPercentage(
          this.args.referenceOccupancy,
        );
    }

    return changes;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentRowLabel() {
    return this.args.currentRowLabel ?? this.intl.t('rates.occupancy.current');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMarketBenchmark() {
    return this.access.hasFeature('otb.market_benchmark');
  }

  // Overridden in the OTB pace tooltip
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTooltipContent() {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    if (this.args.occupancy?.dateAggregation === DATE_AGGREGATION.WEEK) {
      const dateFormatted = this.args.occupancy?.day?.date?.format('LL');
      return this.intl.t('general.week_of', { date: dateFormatted });
    }
    return this.args.occupancy?.day?.date?.format('dddd LL');
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable no-whitespace-for-layout }}
    <div class="rates-table-tooltip-container">
      <PrismTooltipHeader>
        <span class="pr-200 pl-200">
          {{this.title}}
        </span>
      </PrismTooltipHeader>
      {{#if this.showTooltipContent}}
        <table cellspacing="0">
          <thead>
            <tr>
              <th></th>
              <th class="text-200--bold">
                <div>{{t "rates.my_OTB"}}</div>
                {{! Note: if OTB data is missing, don't show the update timestamp (isMissing indicates this). }}
                {{#if (and @showUpdateTime (not @occupancy.isMissing))}}
                  <div
                    class="update-time"
                  >({{@occupancy.otbUpdateTimestamp}})</div>
                {{/if}}
              </th>
              {{#if (hasFeature "pms.transient_group_split")}}
                <th class="text-200--bold">{{t
                    "rates.booking_curve.individual"
                  }}</th>
                <th class="text-200--bold">{{t
                    "rates.booking_curve.groups"
                  }}</th>
              {{/if}}
              {{#if @showAvailableRooms}}
                <th class="text-200--bold">{{t "rates.available_rooms"}}</th>
              {{/if}}
              {{#if @showCapacity}}
                <th class="text-200--bold">{{t
                    "rates.booking_curve.capacity"
                  }}</th>
              {{/if}}
              {{#if this.showMarketBenchmark}}
                <th class="text-200--bold">
                  <div>{{t "rates.tooltip.market_otb"}}</div>
                  {{! Note: if Market OTB data is missing, don't show the update timestamp (isBenchmarkMissing indicates this). }}
                  {{#if
                    (and @showUpdateTime (not @occupancy.isBenchmarkMissing))
                  }}
                    <div
                      class="update-time"
                    >({{@occupancy.marketOtbUpdateTimestamp}})</div>
                  {{/if}}
                </th>
              {{/if}}
              {{#if @showShortTermRental}}
                <th class="text-200--bold">
                  <div>{{t "short_term_rental.otb"}}</div>
                </th>
              {{/if}}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{this.currentRowLabel}}</td>
              <td>
                {{#if @occupancy.isMissing}}
                  {{! If My OTB is missing, show -- everywhere }}
                  {{t "general.not_available_short"}}
                  &nbsp; ({{t "general.not_available_short"}}/{{t
                    "general.not_available_short"
                  }})
                {{else}}
                  {{formatOtbValue
                    @occupancy.occupancyPercentage
                    style="percent"
                  }}
                  &nbsp; ({{formatOtbValue
                    @occupancy.roomsBooked
                  }}/{{formatOtbValue @occupancy.totalCapacity}})
                {{/if}}
              </td>
              {{#if (hasFeature "pms.transient_group_split")}}
                <td>{{formatOtbValue @occupancy.roomsBookedIndividual}}</td>
                <td>{{formatOtbValue @occupancy.roomsBookedGroup}}</td>
              {{/if}}
              {{#if @showAvailableRooms}}
                <td>
                  {{formatOtbValue @occupancy.availableRooms}}
                  &nbsp;
                  {{! Don't show excl. OOO if roomsOutOfOrder is null or 0 }}
                  {{#if @occupancy.shouldShowRoomsOutOfOrder}}
                    ({{t
                      "rates.tooltip.excl_ooo"
                      count=@occupancy.roomsOutOfOrder
                    }})
                  {{/if}}
                </td>
              {{/if}}
              {{#if @showCapacity}}
                <td>{{formatOtbValue @occupancy.totalCapacity}}</td>
              {{/if}}
              {{#if this.showMarketBenchmark}}
                <td>{{formatOtbValue
                    @occupancy.benchmarkValueNormalized
                    style="percent"
                  }}</td>
              {{/if}}
              {{#if @showShortTermRental}}
                <td>{{@rentalOccupancy.formattedValue}}</td>
              {{/if}}
            </tr>
            {{#if this.changes}}
              <tr>
                <td
                  data-test-id="occupancy-tooltip-comparison-title"
                >{{this.changeRowLabel}}</td>
                <td class={{changeColorClass this.changes.roomsBooked}}>
                  {{formatOtbValue
                    this.changes.occupancy
                    signDisplay="always"
                    style="percent"
                  }}
                  &nbsp; ({{formatOtbValue
                    this.changes.roomsBooked
                    signDisplay="always"
                  }})
                </td>
                {{#if (hasFeature "pms.transient_group_split")}}
                  <td
                    class={{changeColorClass
                      this.changes.roomsBookedIndividual
                    }}
                  >
                    {{formatOtbValue
                      this.changes.roomsBookedIndividual
                      signDisplay="always"
                    }}
                  </td>
                  <td class={{changeColorClass this.changes.roomsBookedGroup}}>
                    {{formatOtbValue
                      this.changes.roomsBookedGroup
                      signDisplay="always"
                    }}
                  </td>
                {{/if}}
                {{#if @showAvailableRooms}}
                  <td
                    class={{changeColorClass
                      this.changes.availableRooms
                      invertColors=true
                    }}
                  >
                    {{formatOtbValue
                      this.changes.availableRooms
                      signDisplay="always"
                    }}
                  </td>
                {{/if}}
                {{#if @showCapacity}}
                  <td></td>
                {{/if}}
                {{#if this.showMarketBenchmark}}
                  <td
                    class={{changeColorClass
                      this.changes.benchmarkValue
                      threshold=0.005
                    }}
                  >
                    {{formatOtbValue
                      this.changes.benchmarkValue
                      signDisplay="always"
                      style="percent"
                    }}
                  </td>
                {{/if}}
              </tr>
            {{/if}}
            {{#if (and @showReferenceRow @referenceOccupancy)}}
              <tr>
                <td>{{this.referenceRowLabel}}</td>
                <td>
                  {{#if @referenceOccupancy.isMissing}}
                    {{! If My OTB is missing, show -- everywhere }}
                    {{t "general.not_available_short"}}
                    &nbsp; ({{t "general.not_available_short"}}/{{t
                      "general.not_available_short"
                    }})
                  {{else}}
                    {{formatOtbValue
                      @referenceOccupancy.occupancyPercentage
                      style="percent"
                    }}
                    &nbsp; ({{formatOtbValue
                      @referenceOccupancy.roomsBooked
                    }}/{{formatOtbValue @referenceOccupancy.totalCapacity}})
                  {{/if}}
                </td>
                {{#if (hasFeature "pms.transient_group_split")}}
                  <td>{{formatOtbValue
                      @referenceOccupancy.roomsBookedIndividual
                    }}</td>
                  <td>{{formatOtbValue
                      @referenceOccupancy.roomsBookedGroup
                    }}</td>
                {{/if}}
                {{#if @showAvailableRooms}}
                  <td>
                    {{formatOtbValue @referenceOccupancy.availableRooms}}
                    &nbsp;
                    {{! Don't show excl. OOO if roomsOutOfOrder is null or 0 }}
                    {{#if @referenceOccupancy.shouldShowRoomsOutOfOrder}}
                      ({{t
                        "rates.tooltip.excl_ooo"
                        count=@referenceOccupancy.roomsOutOfOrder
                      }})
                    {{/if}}
                  </td>
                {{/if}}
                {{#if @showCapacity}}
                  <td>{{formatOtbValue @referenceOccupancy.totalCapacity}}</td>
                {{/if}}
                {{#if this.showMarketBenchmark}}
                  <td>{{formatOtbValue
                      @referenceOccupancy.benchmarkValueNormalized
                      style="percent"
                    }}</td>
                {{/if}}
                {{#if @showShortTermRental}}
                  <td>{{@rentalReferenceOccupancy.formattedValue}}</td>
                {{/if}}
              </tr>
            {{/if}}
          </tbody>
        </table>
      {{/if}}
    </div>
  </template>
}
